// src/pages/PublicCatalog/components/ProductCard.js
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    IconButton,
    Chip,
    Box,
    Button
} from '@material-ui/core';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon,
    Info as InfoIcon
} from '@material-ui/icons';
import { formatPrice } from '../utils';
import { getBackendUrl } from "../../../config";

const useStyles = makeStyles((theme) => ({
    productCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[4],
        },
    },
    mediaContainer: {
        position: 'relative',
        paddingTop: '75%', // 4:3 Aspect Ratio
    },
    media: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundSize: 'contain',
        backgroundColor: theme.palette.grey[100],
    },
    navigationArrow: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: 4,
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
        },
        zIndex: 2,
    },
    leftArrow: {
        left: theme.spacing(1),
    },
    rightArrow: {
        right: theme.spacing(1),
    },
    cardContent: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    price: {
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        fontSize: '1.2rem',
        marginTop: theme.spacing(1),
    },
    description: {
        marginTop: theme.spacing(1),
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    attributes: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    detailsButton: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
        },
    },
    quantityControl: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: 'auto',
        paddingTop: theme.spacing(2),
    },
    quantity: {
        minWidth: '40px',
        textAlign: 'center',
    },
    viewDetailsButton: {
        marginTop: 'auto',
        marginBottom: theme.spacing(1)
    }
}));

const ProductCard = ({
    product,
    onShowDetails,
    currentImageIndex = 0,
    onImageNavigate,
    cartQuantity = 0,
    onAddToCart,
    onRemoveFromCart,
    onViewProduct // Added this prop
}) => {
    const classes = useStyles();

    return (
        <Card className={classes.productCard}>
            <div className={classes.mediaContainer}>
                {product.images && product.images.length > 0 ? (
                    <>
                        <CardMedia
                            className={classes.media}
                            image={`${getBackendUrl()}public/products/${product.images[currentImageIndex].imageUrl}`}
                            title={product.name}
                        />
                        {product.images.length > 1 && (
                            <>
                                <IconButton
                                    className={`${classes.navigationArrow} ${classes.leftArrow}`}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onImageNavigate(product.id, 'prev');
                                    }}
                                    disabled={currentImageIndex === 0}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                                <IconButton
                                    className={`${classes.navigationArrow} ${classes.rightArrow}`}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onImageNavigate(product.id, 'next');
                                    }}
                                    disabled={currentImageIndex === product.images.length - 1}
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                            </>
                        )}
                        <IconButton
                            className={classes.detailsButton}
                            size="small"
                            onClick={() => onShowDetails(product)}
                        >
                            <InfoIcon />
                        </IconButton>
                    </>
                ) : (
                    <div className={classes.media}>
                        <Typography variant="body2" align="center">
                            Sin imagen
                        </Typography>
                    </div>
                )}
            </div>
            <CardContent className={classes.cardContent}>
                <Typography variant="h6" component="h2">
                    {product.name}
                </Typography>
                
                {product.category && (
                    <Chip
                        size="small"
                        label={product.category.name}
                        color="primary"
                        variant="outlined"
                        style={{ marginTop: 4, marginBottom: 4 }}
                    />
                )}
                
                <Typography className={classes.price}>
                    {new Intl.NumberFormat('es-ES', {
                        style: 'currency',
                        currency: product.currency || 'USD'
                    }).format(product.price)}
                </Typography>
                
                {product.description && (
                    <Typography variant="body2" color="textSecondary" className={classes.description}>
                        {product.description}
                    </Typography>
                )}
                
                {product.attributes && product.attributes.length > 0 && (
                    <Box className={classes.attributes}>
                        {product.attributes.slice(0, 2).map((attr, index) => (
                            <Chip
                                key={index}
                                label={`${attr.name}: ${attr.value}`}
                                size="small"
                                className={classes.chip}
                            />
                        ))}
                        {product.attributes.length > 2 && (
                            <Chip
                                label={`+${product.attributes.length - 2}`}
                                size="small"
                                variant="outlined"
                                className={classes.chip}
                            />
                        )}
                    </Box>
                )}
                
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    className={classes.viewDetailsButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onViewProduct) onViewProduct(product.id);
                    }}
                >
                    Ver detalles
                </Button>
                
                <div className={classes.quantityControl}>
                    <IconButton
                        size="small"
                        onClick={() => onRemoveFromCart(product.id)}
                        disabled={!cartQuantity}
                    >
                        <RemoveIcon />
                    </IconButton>
                    <Typography className={classes.quantity}>
                        {cartQuantity}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => onAddToCart(product.id)}
                    >
                        <AddIcon />
                    </IconButton>
                </div>
            </CardContent>
        </Card>
    );
};

export default ProductCard;